import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  openDeviceLogStream,
  DeviceLogEvent,
  StreamStatus,
} from "./deviceLogsStream";
import { maintenanceApi } from "../../../api/maintenance";
import {
  Pause, Play, Trash2, ChevronDown, ChevronRight, Activity,
  Maximize2, Minimize2, ChevronLeft as PageLeft, ChevronRight as PageRight,
  Search, Download, X,
} from "lucide-react";

interface DeviceLogsPanelProps {
  deviceId: number;
  uniqueName: string | null;
  tenantId?: string;
}

interface LogEntry extends DeviceLogEvent {
  _id: number;
}

interface HistoricalEntry {
  id: number;
  event_time: string;
  event_meta: Record<string, unknown>;
  connector_name: string;
  notification_type: string;
}

interface NotificationType {
  id: number;
  name: string;
  description: string;
}

const MAX_BUFFER = 500;

const NOTIFICATION_COLORS: Record<string, { bg: string; fg: string }> = {
  StatusNotification: { bg: "#e3f2fd", fg: "#1565c0" },
  StartTransaction: { bg: "#e8f5e9", fg: "#2e7d32" },
  StopTransaction: { bg: "#fff3e0", fg: "#e65100" },
  MeterValues: { bg: "#f3e5f5", fg: "#6a1b9a" },
  HeartBeat: { bg: "#f5f5f5", fg: "#616161" },
  BootNotification: { bg: "#e0f7fa", fg: "#00838f" },
  WsDisconnect: { bg: "#ffebee", fg: "#c62828" },
  ChangeAvailability: { bg: "#fffde7", fg: "#f9a825" },
};
const DEFAULT_COLOR = { bg: "#eeeeee", fg: "#424242" };

const STATUS_LABEL: Record<StreamStatus, { label: string; color: string }> = {
  connecting: { label: "Connecting…", color: "#e65100" },
  open: { label: "Live", color: "#2e7d32" },
  closed: { label: "Closed", color: "#616161" },
  error: { label: "Reconnecting…", color: "#c62828" },
};

type TabMode = "live" | "history";

const DeviceLogsPanel: React.FC<DeviceLogsPanelProps> = ({ deviceId, uniqueName, tenantId }) => {
  const [tab, setTab] = useState<TabMode>("live");
  const [fullscreen, setFullscreen] = useState(false);

  // --- Live state ---
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [liveFilter, setLiveFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [autoScroll, setAutoScroll] = useState(true);
  const pausedRef = useRef(paused);
  const nextIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // --- History state ---
  const [historyLogs, setHistoryLogs] = useState<HistoricalEntry[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(50);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTypeFilter, setHistoryTypeFilter] = useState<string>("");
  const [historyStartTime, setHistoryStartTime] = useState("");
  const [historyEndTime, setHistoryEndTime] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyConnector, setHistoryConnector] = useState("");
  const [historyExpanded, setHistoryExpanded] = useState<Set<number>>(new Set());
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([]);
  const [connectorNames, setConnectorNames] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const appendLog = useCallback((event: DeviceLogEvent) => {
    if (pausedRef.current) return;
    setLogs((prev) => {
      const entry: LogEntry = { ...event, _id: nextIdRef.current++ };
      return prev.length >= MAX_BUFFER ? [...prev.slice(prev.length - MAX_BUFFER + 1), entry] : [...prev, entry];
    });
  }, []);

  // Open SSE stream
  useEffect(() => {
    const controller = new AbortController();
    setLogs([]); setExpanded(new Set()); nextIdRef.current = 0; setStatus("connecting");
    openDeviceLogStream(deviceId, { signal: controller.signal, onLog: appendLog, onStatus: setStatus }, tenantId)
      .catch((err) => { if (!controller.signal.aborted) console.error("Device log stream error:", err); });
    return () => { controller.abort(); };
  }, [deviceId, tenantId, appendLog]);

  // Fetch notification types once
  useEffect(() => {
    const fetch = tenantId
      ? maintenanceApi.getNotificationTypesTenant()
      : maintenanceApi.getNotificationTypes();
    fetch.then((res) => setNotificationTypes(res.data)).catch(console.error);
  }, [tenantId]);

  // Fetch historical logs — only on explicit search or page change, NOT on every filter keystroke
  const [historySearchTrigger, setHistorySearchTrigger] = useState(0);

  const buildHistoryParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (historyTypeFilter) params.notification_type_id = parseInt(historyTypeFilter);
    if (historyStartTime) params.start_time = new Date(historyStartTime).toISOString();
    if (historyEndTime) params.end_time = new Date(historyEndTime).toISOString();
    if (historySearch.trim()) params.search = historySearch.trim();
    if (historyConnector) params.connector_name = historyConnector;
    return params;
  }, [historyTypeFilter, historyStartTime, historyEndTime, historySearch, historyConnector]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { ...buildHistoryParams(), page: historyPage, page_size: historyPageSize };

      const res = tenantId
        ? await maintenanceApi.getHistoricalLogsTenant(tenantId, deviceId, params)
        : await maintenanceApi.getHistoricalLogs(deviceId, params);

      setHistoryLogs(res.data.items);
      setHistoryTotal(res.data.total);
      setHistoryTotalPages(res.data.total_pages);

      // Build connector dropdown from results
      const names = new Set<string>();
      res.data.items.forEach((l: HistoricalEntry) => { if (l.connector_name) names.add(l.connector_name); });
      setConnectorNames((prev) => {
        const merged = new Set(prev);
        names.forEach((n) => merged.add(n));
        return Array.from(merged).sort();
      });
    } catch (err) {
      console.error("Failed to fetch historical logs:", err);
    } finally {
      setHistoryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, tenantId, historyPage, historyPageSize, historySearchTrigger]);

  // Auto-fetch on tab switch (initial load) and page change
  useEffect(() => {
    if (tab === "history") fetchHistory();
  }, [tab, fetchHistory]);

  const handleHistorySearch = () => {
    setHistoryPage(1);
    setHistorySearchTrigger((n) => n + 1);
  };

  const handleClearFilters = () => {
    setHistoryTypeFilter("");
    setHistoryStartTime("");
    setHistoryEndTime("");
    setHistorySearch("");
    setHistoryConnector("");
    setHistoryPage(1);
    setHistorySearchTrigger((n) => n + 1);
  };

  const applyDatePreset = (hours: number) => {
    const now = new Date();
    const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
    const toLocal = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setHistoryStartTime(toLocal(from));
    setHistoryEndTime(toLocal(now));
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = buildHistoryParams();
      const res = await maintenanceApi.exportHistoricalLogs(deviceId, params);
      const rows = res.data.items as HistoricalEntry[];
      if (!rows.length) { alert("No data to export"); return; }

      const csvHeader = "ID,Time,Notification Type,Connector,Event Meta\n";
      const csvRows = rows.map((r) => {
        const time = toUTCDate(r.event_time).toISOString();
        const meta = JSON.stringify(r.event_meta).replace(/"/g, '""');
        return `${r.id},"${time}","${r.notification_type}","${r.connector_name}","${meta}"`;
      }).join("\n");

      const blob = new Blob([csvHeader + csvRows], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `device_${deviceId}_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export logs");
    } finally {
      setExporting(false);
    }
  };

  // Live auto-scroll
  useEffect(() => {
    if (!autoScroll || tab !== "live") return;
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs, autoScroll, tab]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 20);
  }, []);

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleHistoryExpanded = (id: number) => {
    setHistoryExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const liveNotificationTypes = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((l) => set.add(l.notification_type));
    return Array.from(set).sort();
  }, [logs]);

  const filteredLogs = useMemo(() => {
    if (!liveFilter) return logs;
    return logs.filter((l) => l.notification_type === liveFilter);
  }, [logs, liveFilter]);

  // DB stores UTC naive timestamps (no Z suffix). Append Z so JS Date
  // parses as UTC and toLocaleString converts to the user's local time.
  const toUTCDate = (iso: string) => new Date(iso.endsWith("Z") ? iso : iso + "Z");

  const formatTime = (iso: string) => {
    try { return toUTCDate(iso).toLocaleTimeString(); } catch { return iso; }
  };
  const formatDateTime = (iso: string) => {
    try { return toUTCDate(iso).toLocaleString(); } catch { return iso; }
  };

  const statusInfo = STATUS_LABEL[status];

  const renderBadge = (type: string) => {
    const color = NOTIFICATION_COLORS[type] || DEFAULT_COLOR;
    return (
      <span style={{ background: color.bg, color: color.fg, padding: "1px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 600, whiteSpace: "nowrap" }}>
        {type}
      </span>
    );
  };

  const panelStyle: React.CSSProperties = fullscreen
    ? { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: "#111", padding: "16px", display: "flex", flexDirection: "column", overflow: "hidden" }
    : {};

  const logContainerHeight = fullscreen ? "calc(100vh - 130px)" : "500px";

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3 style={{ margin: 0, color: fullscreen ? "#e0e0e0" : undefined }}>
            <Activity size={16} style={{ verticalAlign: "middle", marginRight: "6px" }} />
            Device Logs
          </h3>
          {tab === "live" && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, background: statusInfo.color + "20", color: statusInfo.color }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusInfo.color, display: "inline-block" }} />
              {statusInfo.label}
            </span>
          )}
          {uniqueName && <code style={{ fontSize: "12px", color: fullscreen ? "#888" : "#666" }}>{uniqueName}</code>}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Tabs */}
          <div style={{ display: "flex", borderRadius: "6px", overflow: "hidden", border: "1px solid #444" }}>
            <button onClick={() => setTab("live")} style={{ padding: "4px 12px", fontSize: "12px", fontWeight: 600, border: "none", cursor: "pointer", background: tab === "live" ? "#5554d3" : fullscreen ? "#222" : "#f5f5f5", color: tab === "live" ? "#fff" : fullscreen ? "#ccc" : "#333" }}>
              Live
            </button>
            <button onClick={() => setTab("history")} style={{ padding: "4px 12px", fontSize: "12px", fontWeight: 600, border: "none", cursor: "pointer", background: tab === "history" ? "#5554d3" : fullscreen ? "#222" : "#f5f5f5", color: tab === "history" ? "#fff" : fullscreen ? "#ccc" : "#333" }}>
              History
            </button>
          </div>
          <button className="maint-btn-small" onClick={() => setFullscreen(!fullscreen)} title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </div>

      {/* Live tab */}
      {tab === "live" && (
        <>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
            <select value={liveFilter} onChange={(e) => setLiveFilter(e.target.value)} style={{ padding: "4px 8px", fontSize: "13px" }}>
              <option value="">All types ({logs.length})</option>
              {liveNotificationTypes.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
            <button className="maint-btn-small" onClick={() => setPaused((p) => !p)}>
              {paused ? <><Play size={14} /> Resume</> : <><Pause size={14} /> Pause</>}
            </button>
            <button className="maint-btn-small" onClick={() => { setLogs([]); setExpanded(new Set()); }}>
              <Trash2 size={14} /> Clear
            </button>
          </div>

          <div ref={containerRef} onScroll={handleScroll} style={{ background: "#1a1a2e", color: "#e0e0e0", borderRadius: "8px", padding: "8px", maxHeight: logContainerHeight, overflowY: "auto", fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontSize: "12px", flex: fullscreen ? 1 : undefined }}>
            {filteredLogs.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#888" }}>
                {paused ? "Paused — resume to see new logs" : "Waiting for messages…"}
              </div>
            ) : filteredLogs.map((log) => {
              const isExp = expanded.has(log._id);
              return (
                <div key={log._id} style={{ padding: "6px 8px", borderBottom: "1px solid #2a2a40", cursor: "pointer" }} onClick={() => toggleExpanded(log._id)}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {isExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span style={{ color: "#888", minWidth: 70 }}>{formatTime(log.event_time)}</span>
                    {renderBadge(log.notification_type)}
                    <span style={{ color: "#7aa8ff" }}>conn {log.connector_id}</span>
                    {log.session_event_name && <span style={{ color: "#a0a0b0", fontStyle: "italic" }}>{log.session_event_name}</span>}
                  </div>
                  {isExp && (
                    <pre style={{ margin: "6px 0 0 20px", padding: "8px", background: "#0f0f20", borderRadius: "4px", color: "#b0d4ff", overflow: "auto", maxHeight: "300px" }}>
                      {JSON.stringify(log.meta_data, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
          {!autoScroll && <div style={{ marginTop: "6px", fontSize: "11px", color: "#e65100", textAlign: "center" }}>Auto-scroll paused — scroll to bottom to resume</div>}
        </>
      )}

      {/* History tab */}
      {tab === "history" && (
        <>
          {/* Filter row 1: search + type + connector */}
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end", marginBottom: "6px", flexWrap: "wrap" }}>
            <label style={{ fontSize: "12px", flex: 1, minWidth: "180px" }}>
              Search event data
              <div style={{ display: "flex", position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: "8px", top: "8px", color: "#888" }} />
                <input
                  type="text" placeholder="e.g. Charging, errorCode, RFID..."
                  value={historySearch} onChange={(e) => setHistorySearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleHistorySearch(); }}
                  style={{ display: "block", padding: "4px 8px 4px 28px", fontSize: "13px", width: "100%" }}
                />
              </div>
            </label>
            <label style={{ fontSize: "12px" }}>
              Type
              <select value={historyTypeFilter} onChange={(e) => setHistoryTypeFilter(e.target.value)} style={{ display: "block", padding: "4px 8px", fontSize: "13px" }}>
                <option value="">All types</option>
                {notificationTypes.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </label>
            <label style={{ fontSize: "12px" }}>
              Connector
              <select value={historyConnector} onChange={(e) => setHistoryConnector(e.target.value)} style={{ display: "block", padding: "4px 8px", fontSize: "13px" }}>
                <option value="">All connectors</option>
                {connectorNames.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </label>
          </div>
          {/* Filter row 2: date range + presets + actions */}
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end", marginBottom: "8px", flexWrap: "wrap" }}>
            <label style={{ fontSize: "12px" }}>
              From
              <input type="datetime-local" value={historyStartTime} onChange={(e) => setHistoryStartTime(e.target.value)} style={{ display: "block", padding: "4px 8px", fontSize: "13px" }} />
            </label>
            <label style={{ fontSize: "12px" }}>
              To
              <input type="datetime-local" value={historyEndTime} onChange={(e) => setHistoryEndTime(e.target.value)} style={{ display: "block", padding: "4px 8px", fontSize: "13px" }} />
            </label>
            <div style={{ display: "flex", gap: "4px", alignItems: "flex-end" }}>
              {[{ label: "1h", hours: 1 }, { label: "24h", hours: 24 }, { label: "7d", hours: 168 }].map((p) => (
                <button key={p.label} className="maint-btn-small" onClick={() => applyDatePreset(p.hours)} style={{ fontSize: "11px", padding: "4px 8px" }}>
                  {p.label}
                </button>
              ))}
            </div>
            <button className="maint-btn-primary" onClick={handleHistorySearch} style={{ height: "30px", padding: "0 16px", fontSize: "13px" }}>
              <Search size={14} /> Search
            </button>
            <button className="maint-btn-small" onClick={handleClearFilters} title="Clear all filters" style={{ height: "30px" }}>
              <X size={14} /> Clear
            </button>
            <button className="maint-btn-small" onClick={handleExportCsv} disabled={exporting || historyTotal === 0} title="Export as CSV" style={{ height: "30px" }}>
              <Download size={14} /> {exporting ? "Exporting..." : "CSV"}
            </button>
          </div>

          <div style={{ background: "#1a1a2e", color: "#e0e0e0", borderRadius: "8px", padding: "8px", maxHeight: logContainerHeight, overflowY: "auto", fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontSize: "12px", flex: fullscreen ? 1 : undefined }}>
            {historyLoading ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#888" }}>Loading...</div>
            ) : historyLogs.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#888" }}>No historical logs found</div>
            ) : historyLogs.map((log) => {
              const isExp = historyExpanded.has(log.id);
              return (
                <div key={log.id} style={{ padding: "6px 8px", borderBottom: "1px solid #2a2a40", cursor: "pointer" }} onClick={() => toggleHistoryExpanded(log.id)}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {isExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span style={{ color: "#888", minWidth: 140 }}>{formatDateTime(log.event_time)}</span>
                    {renderBadge(log.notification_type)}
                    <span style={{ color: "#7aa8ff" }}>conn {log.connector_name}</span>
                  </div>
                  {isExp && (
                    <pre style={{ margin: "6px 0 0 20px", padding: "8px", background: "#0f0f20", borderRadius: "4px", color: "#b0d4ff", overflow: "auto", maxHeight: "300px" }}>
                      {JSON.stringify(log.event_meta, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", fontSize: "13px", color: fullscreen ? "#aaa" : "#666" }}>
            <span>{historyTotal} entries</span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button className="maint-btn-small" disabled={historyPage <= 1} onClick={() => setHistoryPage(historyPage - 1)}><PageLeft size={14} /></button>
              <span>Page {historyPage} of {historyTotalPages || 1}</span>
              <button className="maint-btn-small" disabled={historyPage >= historyTotalPages} onClick={() => setHistoryPage(historyPage + 1)}><PageRight size={14} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DeviceLogsPanel;
